<div class="@container h-full w-full">
  @if (config().cards.length === 0) {
    <div>
      <p class="text-text-secondary mt-4 text-center">
        {{ config().noDataMessage }}
      </p>
    </div>
  } @else {
    <div class="flex flex-col gap-5">
      @if (pinnedShortcuts().length > 0) {
        <!-- As many fixed-size tiles as fit the list's width: tied to the card columns
           instead, a narrow list stretched three tiles across the whole row. -->
        <ul
          [attr.aria-label]="config().pinnedLabel"
          data-testid="pinned-shortcuts"
          cdkDropList
          cdkDropListOrientation="mixed"
          [cdkDropListDisabled]="!pinnedReorderEnabled()"
          (cdkDropListDropped)="dropPinned($event)"
          class="grid grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))] gap-2.5">
          @for (card of pinnedShortcuts(); track card.id) {
            <li
              cdkDrag
              [cdkDragDisabled]="!pinnedReorderEnabled()"
              [class.cursor-move]="pinnedReorderEnabled()"
              class="group border-accent-border bg-accent-interactive hover:bg-accent-interactive-hover relative aspect-square rounded-lg border transition-colors">
              @if (pinnedReorderEnabled()) {
                <div
                  class="border-accent-main bg-accent-interactive text-accent-main flex aspect-square items-center justify-center rounded-lg border-2 border-dashed"
                  style="animation: cards-list-slot-pulse 1.4s ease-in-out infinite"
                  *cdkDragPlaceholder>
                  <i [class]="ICONS.arrowsOutCardinal" class="text-base opacity-70"></i>
                </div>
              }
              @if (card.route) {
                <a
                  [routerLink]="card.route"
                  class="focus-visible:ring-accent-main block h-full w-full rounded-lg outline-none focus-visible:ring-2">
                  <ng-container
                    *ngTemplateOutlet="
                      pinnedShortcutContent;
                      context: { $implicit: card }
                    " />
                </a>
              } @else {
                <button
                  type="button"
                  (click)="handleCardClick(card)"
                  class="focus-visible:ring-accent-main block h-full w-full cursor-pointer rounded-lg text-left outline-none focus-visible:ring-2">
                  <ng-container
                    *ngTemplateOutlet="
                      pinnedShortcutContent;
                      context: { $implicit: card }
                    " />
                </button>
              }
              <!-- Overlaid as a sibling (not nested in the anchor) to keep valid markup -->
              <button
                type="button"
                (click)="togglePin($event, card)"
                [attr.aria-label]="config().unpinTooltip"
                [title]="config().unpinTooltip"
                class="text-accent-main hover:text-accent-hover focus-visible:ring-accent-main absolute top-1.5 right-1.5 cursor-pointer rounded p-1 outline-none focus-visible:ring-2">
                <i [class]="ICONS.pushPinFill" class="block text-xs"></i>
              </button>
            </li>
          }
        </ul>
      }
      <div
        cdkDropList
        cdkDropListOrientation="mixed"
        [cdkDropListDisabled]="!reorderEnabled()"
        (cdkDropListDropped)="drop($event)"
        class="grid grid-cols-1 gap-2.5 @min-[40rem]:grid-cols-2 @min-[48rem]:grid-cols-3 @min-[64rem]:grid-cols-4">
        @for (card of displayCards(); track card.id) {
          <div
            cdkDrag
            [cdkDragDisabled]="!reorderEnabled()"
            class="group relative"
            [class.cursor-move]="reorderEnabled()">
            @if (reorderEnabled()) {
              <!-- Landing slot: a pulsing, accent-tinted dashed outline that marks
                 exactly where the card will drop. -->
              <div
                class="border-accent-main bg-accent-interactive text-accent-main flex h-full min-h-24 items-center justify-center gap-2 rounded-lg border-2 border-dashed"
                style="animation: cards-list-slot-pulse 1.4s ease-in-out infinite"
                *cdkDragPlaceholder>
                <i [class]="ICONS.arrowsOutCardinal" class="text-base opacity-70"></i>
                <span class="text-xs font-semibold">
                  {{ card.title }}
                </span>
              </div>
              <!-- Compact card that follows the cursor while dragging -->
              <div
                class="bg-elevated border-accent-main ring-accent-main/25 flex items-center gap-2.5 rounded-lg border-2 p-4 ring-4"
                *cdkDragPreview>
                <div
                  class="bg-accent-interactive text-accent-main flex h-7 w-7 shrink-0 items-center justify-center rounded">
                  <i [class]="card.iconClass" class="text-sm"></i>
                </div>
                <div class="text-text-primary truncate text-[13px] font-semibold">
                  {{ card.title }}
                </div>
              </div>
            }
            @if (card.route) {
              <!-- Routing card: native anchor tag for proper link behavior -->
              <a
                [routerLink]="card.route"
                class="bg-surface border-default hover:bg-interactive duration-fast focus:ring-accent-main block cursor-pointer rounded-lg border p-4 transition-all outline-none hover:shadow-xs focus:ring-2 focus:ring-inset">
                <ng-container
                  *ngTemplateOutlet="cardContent; context: { $implicit: card }" />
              </a>
            } @else {
              <!-- Interactive card: div with click handler for local state management -->
              <div
                role="button"
                tabindex="0"
                class="bg-surface border-default hover:bg-interactive transition-add focus:ring-accent-main cursor-pointer rounded-lg border p-4 outline-none hover:shadow-xs focus:ring-2 focus:ring-inset"
                (click)="handleCardClick(card)"
                (keydown.enter)="handleCardClick(card)"
                (keydown.space)="handleCardClick(card); $event.preventDefault()">
                <ng-container
                  *ngTemplateOutlet="cardContent; context: { $implicit: card }" />
              </div>
            }
            @if (pinningEnabled()) {
              <!-- Overlaid as a sibling (not nested in the anchor) to keep valid markup -->
              <button
                type="button"
                (click)="togglePin($event, card)"
                [attr.aria-pressed]="card.isPinned"
                [attr.aria-label]="
                  card.isPinned ? config().unpinTooltip : config().pinTooltip
                "
                [title]="card.isPinned ? config().unpinTooltip : config().pinTooltip"
                [ngClass]="
                  card.isPinned
                    ? 'text-accent-main hover:text-accent-hover'
                    : 'text-text-tertiary hover:text-accent-main opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                "
                class="duration-fast focus-visible:ring-accent-main hover:bg-interactive absolute top-3 right-3 cursor-pointer rounded p-1 transition-all outline-none hover:scale-110 focus-visible:ring-2 active:scale-95">
                <i
                  [class]="card.isPinned ? ICONS.pushPinFill : ICONS.pushPin"
                  class="block text-sm"></i>
              </button>
            }
          </div>
        }
      </div>
    </div>
  }
</div>

<!-- Shared card content template -->
<ng-template #cardContent let-card>
  <div class="mb-3 flex items-center gap-2.5" [class.pr-6]="pinningEnabled()">
    <div
      class="bg-accent-interactive text-accent-main flex h-7 w-7 shrink-0 items-center justify-center rounded">
      <i [class]="card.iconClass" class="text-sm"></i>
    </div>
    <div class="flex min-w-0 flex-1 items-center gap-1.5">
      <div class="text-text-primary min-w-0 truncate text-[13px] font-semibold">
        {{ card.title }}
      </div>
      @if (card.tooltip) {
        <span #infoTarget data-testid="card-info" class="flex shrink-0 items-center">
          <i
            [class]="ICONS.info"
            class="text-text-tertiary hover:text-text-primary cursor-help text-xs transition-colors"></i>
        </span>
        <app-tooltip
          [target]="infoTarget"
          [title]="card.title"
          [content]="card.tooltip"
          [position]="'top'"
          [minWidth]="200"
          [maxWidth]="390" />
      }
    </div>
  </div>
  <div class="flex items-stretch gap-3.5">
    @for (labels of labelsEntries(card.labels); track labels[0]; let first = $first) {
      @if (!first) {
        <span class="bg-weak w-px shrink-0 self-stretch"></span>
      }
      <div class="flex min-w-0 flex-col gap-0.75">
        <span
          [title]="labels[1]"
          class="text-text-primary truncate font-mono text-[15px] leading-none font-semibold tabular-nums">
          {{ labels[1] }}
        </span>
        <!-- Stats share a narrow card, so a long label (e.g. "Vehicles" next to
             "Total") must ellipsise rather than spill past the card border. -->
        <span
          [title]="labels[0]"
          class="text-text-tertiary truncate text-[10px] font-semibold tracking-[0.06em] uppercase">
          {{ labels[0] }}
        </span>
      </div>
    }
  </div>
</ng-template>

<!-- Pinned shortcut tile: icon on top, then title and total -->
<ng-template #pinnedShortcutContent let-card>
  <div #shortcutTile class="flex h-full w-full flex-col justify-between p-2.5">
    <i [class]="card.iconClass" class="text-accent-main text-lg"></i>
    <div class="flex min-w-0 flex-col gap-0.5">
      <span
        #shortcutTitle
        [attr.data-card-id]="card.id"
        class="text-text-primary line-clamp-2 text-xs leading-tight font-semibold">
        {{ card.title }}
      </span>
      @if (primaryLabelValue(card); as value) {
        <span class="text-text-secondary font-mono text-[11px] leading-none tabular-nums">
          {{ value }}
        </span>
      }
    </div>
  </div>
  <app-tooltip
    [target]="shortcutTile"
    [content]="card.title"
    [disabled]="!truncatedShortcutIds().has(card.id)"
    [position]="'top'"
    [maxWidth]="240" />
</ng-template>
